import { ValidationError } from "sequelize"
import { MtDriveVersionCreate, MtDriveVersion } from "server/models"

import {
    STORAGE_AWS,
    STORAGE_GOOGLE,
    STORAGE_MICROSOFT,
} from "const"
import { OrderType, StorageType, UploadCallback } from "client/utils"
import {
    GET, POST, validate, sort, search, sortByRevision,
} from "client/store/utils"
import {
    AwsUpload,
    AwsDelete,
    GoogleUpload,
    GoogleDelete,
    MicrosoftUpload,
    MicrosoftDelete,

    UserData,
    LangType,
    VersionList,
    VersionValidation,
    DriveVersionFilters,

    initVersionCreate,
} from "client/store"
import { BalloonMessage } from "client/components"

export const LoadDriveVersions = (fileId: number) => (
    GET(`/mtDriveVersion/fileId/${fileId}`)
    .then((versions: MtDriveVersion[]) => {
        const versionList: VersionList = {}
        versions.forEach((version) => {
            versionList[version.id] = version
        })
        return versionList
    })
)

export const LoadDriveVersionById = (versionId: number) => (
    GET(`/mtDriveVersion/id/${versionId}`)
    .then((version: MtDriveVersion) => version || null)
)

export const LoadDriveVersionsByIds = (versionIds: number[]) => (
    POST(
        "/mtDriveVersion/getByIds",
        JSON.stringify(versionIds),
    ).then((versions: MtDriveVersion[]) => versions)
)

export const AddDriveVersion = (version: MtDriveVersionCreate) => (
    POST(
        "/mtDriveVersion/add",
        JSON.stringify(version),
    ).then((res) => {
        const id = Number(res)
        if (id) return id

        BalloonMessage("Error upload. Check console")
        // eslint-disable-next-line no-console
        console.log(res)
        return null
    })
)

export const SaveDriveVersion = (
    version: MtDriveVersion,
    lang: LangType,
) => (
    POST(
        "/mtDriveVersion/save",
        JSON.stringify(version),
    ).then((res) => {
        const error = res as ValidationError
        return {
            validation: <VersionValidation>validate(error, lang),
        }
    })
)

interface UploadVersionProps {
    file: File
    storage: StorageType
    userData: UserData
    fileId: number
    callbackSuccess: (versionId: number) => void
    callbackError: () => void
}
export const UploadVersion = ({
    file,
    storage,
    userData,
    fileId,
    callbackSuccess,
    callbackError,
}: UploadVersionProps) => {
    const { accountId, userId } = userData || {}
    const driveVersion: MtDriveVersionCreate = {
        ...initVersionCreate,
        fileId,
        accountId,
        owner: userId,
        name: file.name,
        type: file.type,
        size: file.size,
    }

    const callback: UploadCallback = (id, sharedUrl) => {
        AddDriveVersion({
            ...driveVersion,
            storageId: id,
            storageSharedUrl: sharedUrl,
        }).then((versionId) => {
            if (versionId) callbackSuccess(fileId)
            else callbackError()
        })
    }

    // AWS S3 upload
    if (storage === STORAGE_AWS) {
        AwsUpload(file, callback)
    }

    // Google Drive upload
    if (storage === STORAGE_GOOGLE) {
        GoogleUpload(file, accountId, callback)
    }

    // MS OneDrive upload
    if (storage === STORAGE_MICROSOFT) {
        MicrosoftUpload(file, accountId, callback)
    }
}

export const DeleteDriveVersions = (
    versions: readonly MtDriveVersion[],
    storage: StorageType,
    accountId: number,
) => {
    const promiseArray: Promise<any>[] = []

    const versionIds: number[] = []
    const awsFileVersions: string[] = []
    const googleFileVersions: string[] = []
    const microsoftFileVersions: string[] = []

    versions.forEach((version) => {
        if (storage === STORAGE_AWS) awsFileVersions.push(version.storageId)
        if (storage === STORAGE_GOOGLE) googleFileVersions.push(version.storageId)
        if (storage === STORAGE_MICROSOFT) microsoftFileVersions.push(version.storageId)

        versionIds.push(version.id)
    })

    // AWS S3 delete
    if (awsFileVersions.length > 0) {
        promiseArray.push(
            AwsDelete(awsFileVersions),
        )
    }

    // Google Drive delete
    if (googleFileVersions.length > 0) {
        promiseArray.push(
            GoogleDelete(accountId, googleFileVersions),
        )
    }

    // MS OneDrive delete
    if (microsoftFileVersions.length > 0) {
        promiseArray.push(
            MicrosoftDelete(accountId, microsoftFileVersions),
        )
    }

    return Promise.all(promiseArray).then((res) => {
        if (res.includes(false)) return null

        return POST(
            "/mtDriveVersion/delete",
            JSON.stringify(versionIds),
        ).then((removedRows: number) => removedRows)
    })
}

export const SortDriveVersions = (
    versions: MtDriveVersion[],
    sortColumn: keyof MtDriveVersion,
    sortOrder: OrderType,
    filters: DriveVersionFilters,
) => {
    const versionsFiltered = versions.filter((version) => {
        let showRow = true
        if (filters.search) showRow = search(filters.search, version)
        return showRow
    })

    if (sortColumn === "revision") {
        versionsFiltered.sort((a, b) => sortByRevision(a.revision, b.revision, sortOrder))
    } else {
        versionsFiltered.sort((a, b) => sort(a, b, sortColumn, sortOrder))
    }

    const versionIds: number[] = []
    versionsFiltered.forEach((version) => versionIds.push(version.id))

    return versionIds
}
